/**
 * Is takibi presenter'i
 */

import { AppState } from '../../modules/state.js';
import { FirebaseService } from '../../shared/services/firebase.service.js';
import { COLLECTIONS, PRIORITY_LABELS, STATUS_LABELS } from '../../firebase-config.js';

interface TasksPresenterDeps {
    openModal: (modalId: string) => void;
    showToast: (message: string, type?: string) => void;
    generateId: () => string;
    escapeHtml: (value: string) => string;
}

export function renderTasksPresenter(deps: TasksPresenterDeps): void {
    const container = document.getElementById('tasks-list');
    if (!container) return;

    const priorityOrder: Record<string, number> = { high: 0, medium: 1, low: 2 };
    const statusIcons: Record<string, string> = { pending: '⏳', in_progress: '🔄', completed: '✅' };
    const renderTaskCard = (task: any) => `
        <div class="task-card">
            <div class="task-status-icon ${task.status}">${statusIcons[task.status]}</div>
            <div class="task-info">
                <h4>${deps.escapeHtml(task.title)} <span class="priority-badge ${task.priority}">${PRIORITY_LABELS[task.priority]}</span></h4>
                <p>${deps.escapeHtml(task.description)}</p>
            </div>
            <div class="task-meta">
                <span class="status-badge-sm ${task.status}">${STATUS_LABELS[task.status]}</span>
                <div class="task-actions">
                    ${task.detail ? `<button class="btn btn-secondary btn-sm" onclick="viewTaskDetail('${task.id}')">👁</button>` : ''}
                    <button class="btn btn-secondary btn-sm" onclick="editTask('${task.id}')">✏️</button>
                    <button class="btn btn-danger btn-sm" onclick="deleteTask('${task.id}')">🗑</button>
                </div>
            </div>
        </div>
    `;

    if (AppState.currentTaskFilter === 'all') {
        const groups = {
            pending: AppState.tasks.filter(task => task.status === 'pending').sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]),
            in_progress: AppState.tasks.filter(task => task.status === 'in_progress').sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]),
            completed: AppState.tasks.filter(task => task.status === 'completed').sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority])
        };

        if (AppState.tasks.length === 0) {
            container.innerHTML = '<p class="empty-state">Henüz iş kaydı eklenmemiş</p>';
            return;
        }

        container.innerHTML = `
            <div class="tasks-kanban">
                <div class="tasks-column">
                    <h4 class="tasks-column-title">⏳ Bekliyor <span>${groups.pending.length}</span></h4>
                    ${groups.pending.length ? groups.pending.map(renderTaskCard).join('') : '<p class="empty-state">Kayıt yok</p>'}
                </div>
                <div class="tasks-column">
                    <h4 class="tasks-column-title">🔄 Devam Ediyor <span>${groups.in_progress.length}</span></h4>
                    ${groups.in_progress.length ? groups.in_progress.map(renderTaskCard).join('') : '<p class="empty-state">Kayıt yok</p>'}
                </div>
                <div class="tasks-column">
                    <h4 class="tasks-column-title">✅ Tamamlandı <span>${groups.completed.length}</span></h4>
                    ${groups.completed.length ? groups.completed.map(renderTaskCard).join('') : '<p class="empty-state">Kayıt yok</p>'}
                </div>
            </div>
        `;
        return;
    }

    const filtered = AppState.tasks
        .filter(task => task.status === AppState.currentTaskFilter)
        .sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);

    if (filtered.length === 0) {
        container.innerHTML = '<p class="empty-state">Bu filtreye uygun iş bulunamadı</p>';
        return;
    }

    container.innerHTML = filtered.map(renderTaskCard).join('');
}

export function addTaskPresenter(data: Record<string, any>, deps: TasksPresenterDeps) {
    const task = { id: deps.generateId(), ...data, createdAt: new Date().toISOString() };
    AppState.tasks.push(task);
    FirebaseService.add(COLLECTIONS.TASKS, task);
    deps.showToast('İş kaydı eklendi', 'success');
    return task;
}

export function updateTaskPresenter(id: string, data: Record<string, any>, deps: TasksPresenterDeps): boolean {
    const index = AppState.tasks.findIndex(task => task.id === id);
    if (index === -1) return false;

    const updated = { ...AppState.tasks[index], ...data };
    AppState.tasks[index] = updated;
    FirebaseService.save(COLLECTIONS.TASKS, id, updated);
    deps.showToast('İş kaydı güncellendi', 'success');
    return true;
}

export function deleteTaskPresenter(id: string, deps: TasksPresenterDeps): void {
    if (!confirm('Bu iş kaydını silmek istediğinizden emin misiniz?')) return;

    AppState.tasks = AppState.tasks.filter(task => task.id !== id);
    FirebaseService.delete(COLLECTIONS.TASKS, id);
    renderTasksPresenter(deps);
    deps.showToast('İş kaydı silindi', 'success');
}

export function editTaskPresenter(id: string, deps: TasksPresenterDeps): void {
    const task = AppState.tasks.find(item => item.id === id);
    if (!task) return;

    document.getElementById('task-modal-title')!.textContent = 'İş Düzenle';
    (document.getElementById('task-id') as HTMLInputElement).value = task.id;
    (document.getElementById('task-title') as HTMLInputElement).value = task.title;
    (document.getElementById('task-description') as HTMLTextAreaElement).value = task.description;
    (document.getElementById('task-detail') as HTMLTextAreaElement).value = task.detail || '';
    (document.getElementById('task-status') as HTMLSelectElement).value = task.status;
    (document.getElementById('task-priority') as HTMLSelectElement).value = task.priority;
    deps.openModal('task-modal');
}

export function viewTaskDetailPresenter(id: string, deps: TasksPresenterDeps): void {
    const task = AppState.tasks.find(item => item.id === id);
    if (!task) return;

    const content = document.getElementById('task-detail-content');
    if (!content) return;

    content.innerHTML = `
        <div class="task-detail-header">
            <h4>${deps.escapeHtml(task.title)}</h4>
            <div class="task-detail-badges">
                <span class="status-badge-sm ${task.status}">${STATUS_LABELS[task.status]}</span>
                <span class="priority-badge ${task.priority}">${PRIORITY_LABELS[task.priority]}</span>
            </div>
        </div>
        <div class="task-detail-content">
            <h5>Açıklama</h5>
            <p>${deps.escapeHtml(task.description)}</p>
        </div>
        ${task.detail ? `
            <div class="task-detail-content">
                <h5>Detaylı Bilgi</h5>
                <p>${deps.escapeHtml(task.detail)}</p>
            </div>
        ` : ''}
    `;
    deps.openModal('task-detail-modal');
}
